import React, { useRef, useEffect, FunctionComponent, CSSProperties } from "react";

/**
 * Hook that alerts clicks outside of the passed ref
 */

type Props = {
  style?: CSSProperties;
  toggle: () => void;
  children?: React.ReactNode;
  excludeElement?: HTMLElement;
};

function useOutsideAlerter(
  ref: React.RefObject<HTMLDivElement>,
  toggle: () => void,
  excludeElement?: HTMLElement
) {
  useEffect(() => {
    /**
     * Alert if clicked on outside of element
     */
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;

      // Check if click is inside the main component
      if (ref.current && ref.current.contains(target)) {
        return;
      }

      // Check if click is on the excluded element
      if (excludeElement && excludeElement.contains(target)) {
        return;
      }

      toggle();
    }

    // Bind the event listener
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, toggle, excludeElement]);
}

/**
 * Component that alerts if you click outside of it
 */
const OutsideAlerter: FunctionComponent<Props> = ({
  style,
  children,
  toggle,
  excludeElement,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  useOutsideAlerter(wrapperRef, toggle, excludeElement);

  return (
    <div ref={wrapperRef} style={style}>
      {children}
    </div>
  );
};

export default OutsideAlerter;